import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Videobook from "../components/Videobook";
import Audiobook from "../components/Audiobook";

// Reuse your AudiobookSkeleton / VideobookSkeleton if you have them.
// If you don’t yet have VideobookSkeleton, you can show the AudiobookSkeleton as a generic.
const CourseShell = () => (
  <div className="p-10 animate-pulse">
    <div className="h-8 w-72 bg-slate-200 rounded mb-6" />
    <div className="h-4 w-48 bg-slate-200 rounded mb-3" />
    <div className="h-4 w-64 bg-slate-200 rounded mb-8" />
    <div className="h-48 bg-slate-200 rounded" />
  </div>
);

export default function CourseDeepLink() {
  const { courseId } = useParams();
  const navigate = useNavigate();

  const [subject, setSubject] = useState(null);
  const [error, setError] = useState(null);

  // Smooth swap state
  const [uiReady, setUiReady] = useState(false);

  useEffect(() => {
    let alive = true;

    (async () => {
      try {
        setError(null);
        setSubject(null);
        setUiReady(false);

        const res = await fetch(
          `${process.env.REACT_APP_USER_API_URL}/subjects/by-course-id/${encodeURIComponent(courseId)}`,
          { method: "GET" }
        );

        if (!res.ok) throw new Error(`Course not found (${res.status})`);

        const json = await res.json();
        if (!alive) return;

        setSubject(json);

        // tiny delay removes harsh swap even on fast connections
        window.setTimeout(() => {
          if (alive) setUiReady(true);
        }, 80);
      } catch (e) {
        if (alive) setError(e.message || "Failed to load course");
      }
    })();

    return () => {
      alive = false;
    };
  }, [courseId]);

  if (error) {
    return (
      <div className="p-10">
        <div className="text-xl font-semibold mb-2">Couldn’t open course</div>
        <div className="text-slate-600 mb-6">{error}</div>
        <button
          className="px-4 py-2 rounded-lg bg-slate-100 border border-slate-300"
          onClick={() => navigate("/")}
        >
          Back to Library
        </button>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen">
      {/* Shell stays mounted and fades out */}
      <div
        className={[
          "absolute inset-0 transition-opacity duration-300",
          uiReady ? "opacity-0 pointer-events-none" : "opacity-100",
        ].join(" ")}
        aria-hidden={uiReady}
      >
        <CourseShell />
      </div>

      {/* Real content fades in */}
      <div
        className={[
          "absolute inset-0 transition-opacity duration-300",
          uiReady ? "opacity-100" : "opacity-0 pointer-events-none",
        ].join(" ")}
        aria-hidden={!uiReady}
      >
        {subject ? (
          subject.type === "Audiobook" ? (
            <Audiobook subject={subject} exerciseHandler={() => navigate("/")} />
          ) : (
            <Videobook subject={subject} exerciseHandler={() => navigate("/")} />
          )
        ) : null}
      </div>
    </div>
  );
}
